import { RefObject, useEffect } from 'react';

function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void
) {
  useEffect(() => {
    const listener = (event: Event) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }

      // Skip if click is in a different modal. Start the search for the
      // ref's own enclosing modal at its parent, not itself: an element
      // (e.g. a portaled dropdown panel) can carry `data-modal-overlay`
      // to shield itself from an unrelated modal's outside-click handler,
      // and that marker shouldn't make this handler treat every click
      // outside the element as "a different modal".
      const target = event.target as HTMLElement;
      const targetModal = target.closest?.('[data-modal-overlay]');
      const refModal = ref.current.parentElement?.closest?.(
        '[data-modal-overlay]'
      );
      if (targetModal && refModal && targetModal !== refModal) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

export default useOnClickOutside;
